import React, { FC } from 'react';
import styled from '@emotion/styled';

import VariationName from './VariationName';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { useStorage } from '~Components/Storage';

const Wrapper = styled.div`
  margin: 0 16px 0 56px;
  color: #585858;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const VariationItem = styled.div<{ deleted?: boolean; markGreen?: boolean }>`
  margin: 0 0 5px 0;
  font-size: 24px;
  border-radius: 5px;
  line-height: 40px;
  color: ${(props) => {
    if (props.deleted) return 'var(--colorUIRed)';
    if (props.markGreen) return 'var(--colorUIGreen)';
    return 'var(--colorBlueGray1200)';
  }};
  font-weight: ${(props) => (props.deleted || props.markGreen ? 600 : 400)};

  span {
    margin-right: 8px;
  }
`;

interface Props {
  /** Id's to help us identify if the variation was preselected or not */
  modificationGroups: string[];
  variations: string[];
  removedVariations: string[];
}

const Variations: FC<Props> = ({ modificationGroups, variations, removedVariations }) => {
  const [storage] = useStorage();
  const itemModifications = useItemModifications(modificationGroups);

  const isPreselected = (id: string) => {
    return itemModifications.modificationGroups.some((group) =>
      group.preselectedVariations.includes(id),
    );
  };

  const getGreenMarkedVariations = (): {
    regularVariations: string[];
    greenMarkedVariations: string[];
  } => {
    if (!storage.markAddedVariationsGreen) {
      return { regularVariations: variations, greenMarkedVariations: [] };
    }

    const preselectedVariations = variations.filter((id) => isPreselected(id));
    const greenMarkedVariations = variations.filter((id) => !isPreselected(id));
    return { regularVariations: preselectedVariations, greenMarkedVariations };
  };

  /** Regular variations is just variations with no styling - greenMarked is the ones that will have a + prefix and green */
  const { regularVariations, greenMarkedVariations } = getGreenMarkedVariations();

  return (
    <Wrapper>
      {removedVariations.map((id) => (
        <VariationItem key={id} deleted>
          <span>-</span>
          <VariationName id={id} deleted />
        </VariationItem>
      ))}

      {regularVariations.map((id) => (
        <VariationItem key={id}>
          <span>·</span>
          <VariationName id={id} />
        </VariationItem>
      ))}

      {greenMarkedVariations.map((id) => (
        <VariationItem key={id} markGreen>
          <span>+</span>
          <VariationName id={id} markGreen />
        </VariationItem>
      ))}
    </Wrapper>
  );
};

export default React.memo(Variations);
